import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { format } from 'date-fns';

const PriceChart = ({ data, newsData }) => {
  // Combine price data with news events
  const chartData = data.map(pricePoint => {
    const dayNews = newsData.filter(news => news.date === pricePoint.date);
    const majorNews = dayNews.filter(news => Math.abs(news.impact) > 0.08);
    
    return {
      ...pricePoint,
      hasNews: dayNews.length > 0,
      majorNews: majorNews.length > 0,
      newsCount: dayNews.length
    };
  });

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const dayNews = newsData.filter(news => news.date === label);
      
      return (
        <div className="bg-white p-4 border border-gray-300 rounded-lg shadow-lg">
          <p className="font-semibold">{format(new Date(label), 'MMM dd, yyyy')}</p>
          <p className="text-xrp-600">Price: ${payload[0].value}</p>
          <p className={`text-sm ${data.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            Change: {(data.change * 100).toFixed(2)}%
          </p>
          {dayNews.length > 0 && (
            <div className="mt-2 border-t pt-2">
              <p className="text-sm font-medium">News Events ({dayNews.length}):</p>
              {dayNews.slice(0, 2).map((news, idx) => (
                <p key={idx} className="text-xs text-gray-600 truncate">
                  • {news.headline}
                </p>
              ))}
              {dayNews.length > 2 && (
                <p className="text-xs text-gray-500">+{dayNews.length - 2} more...</p>
              )}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    if (payload.majorNews) {
      return (
        <circle
          cx={cx}
          cy={cy}
          r={4}
          fill="#ef4444"
          stroke="#ffffff"
          strokeWidth={2}
        />
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-gray-800">XRP Price & News Impact</h3>
        <div className="flex items-center space-x-4 text-sm">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-xrp-500 rounded-full mr-2"></div>
            <span>Price</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
            <span>Major News</span>
          </div>
        </div>
      </div>
      
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="date" 
            tickFormatter={(value) => format(new Date(value), 'MMM dd')}
            stroke="#666"
          />
          <YAxis 
            domain={['dataMin - 0.01', 'dataMax + 0.01']}
            tickFormatter={(value) => `$${value}`}
            stroke="#666"
          />
          <Tooltip content={<CustomTooltip />} />
          <Line 
            type="monotone" 
            dataKey="price" 
            stroke="#0ea5e9" 
            strokeWidth={2}
            dot={<CustomDot />}
            activeDot={{ r: 6, fill: '#0ea5e9' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PriceChart;